import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { profileApi, type PersonalDemand, type GreenCertCreateParams } from '@/services/api';
import {
  formatFieldValue,
  YEAR_OPTIONS,
  HALF_YEAR_OPTIONS,
  MONTH_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  CHANNEL_OPTIONS,
  PROVINCE_OPTIONS,
  SUBSIDY_OPTIONS,
  TRADE_METHOD_OPTIONS,
  EXCHANGE_OPTIONS,
  OFFER_BY_OPTIONS,
  TRADE_PRINCIPLE_OPTIONS,
} from '@/components/feature/GreenCertFieldConfig';

function tagLabel(vals: string[]): string | null {
  if (!vals || vals.length === 0) return null;
  const limit = vals.slice(0, 2);
  return limit.map((v) => {
    if (v === 'non_channel') return '非通道';
    if (v === 'channel') return '通道';
    if (v === 'any') return '均可';
    if (v === 'subsidized') return '补贴';
    if (v === 'non_subsidized') return '非补贴';
    if (v === 'transfer') return '划转';
    if (v === 'certify') return '制证';
    if (v === 'price_priority') return '价格优先';
    if (v === 'time_priority') return '时间优先';
    return v;
  }).join('·');
}

function EditModal({
  item,
  onSaved,
  onClose,
}: {
  item: PersonalDemand;
  onSaved: (updated: PersonalDemand) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<GreenCertCreateParams>({
    direction: 'buy',
    year: item.year,
    halfYear: item.halfYear || [],
    month: item.month || [],
    projectType: item.projectType || [],
    channel: item.channel || [],
    provinceRequirement: item.provinceRequirement || [],
    subsidy: item.subsidy || [],
    quantity: item.quantity,
    price: item.price,
    tradeMethod: item.tradeMethod || [],
    exchange: item.exchange || [],
    offerBy: item.offerBy || [],
    tradePrinciple: item.tradePrinciple || [],
    expiryDate: item.expiryDate,
    otherInfo: item.otherInfo || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (field: keyof GreenCertCreateParams, value: string) => {
    setForm((prev) => {
      const arr = (prev[field] as string[]) || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [field]: next };
    });
  };

  const single = (field: keyof GreenCertCreateParams, value: string) => {
    setForm((prev) => ({ ...prev, [field]: [value] }));
  };

  const chips = (
    field: keyof GreenCertCreateParams,
    label: string,
    options: { value: string; label: string }[],
    multi: boolean,
  ) => {
    const arr = (form[field] as string[]) || [];
    return (
      <div>
        <label className="block text-xs font-medium text-foreground-600 mb-1.5">
          {label}
          {multi && <span className="text-foreground-400 font-normal ml-1">可多选</span>}
        </label>
        <div className="flex flex-wrap gap-1.5">
          {options.map((opt) => {
            const selected = arr.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => (multi ? toggle(field, opt.value) : single(field, opt.value))}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                  selected
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-background-200 text-foreground-500 hover:border-background-300'
                }`}
              >
                {selected && multi && <i className="ri-check-line mr-0.5" />}
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const save = async () => {
    if (!form.year) { setError('请选择年份'); return; }
    if (form.halfYear.length === 0) { setError('请选择半年'); return; }
    if (form.projectType.length === 0) { setError('请选择项目类型'); return; }
    if (form.channel.length === 0) { setError('请选择通道与否'); return; }
    if (form.subsidy.length === 0) { setError('请选择补贴与否'); return; }
    if (!form.quantity || form.quantity <= 0) { setError('请输入有效张数'); return; }
    if (form.tradeMethod.length === 0) { setError('请选择交易方式'); return; }
    if (form.exchange.length === 0) { setError('请选择交易所'); return; }
    if (!form.expiryDate) { setError('请选择信息有效期'); return; }
    setSaving(true);
    setError('');
    try {
      const updated = await profileApi.editDemand(item.id, form);
      onSaved({ ...item, ...(updated as unknown as Partial<PersonalDemand>) });
    } catch {
      setError('保存失败，请重试');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl p-5 w-full max-w-lg border border-background-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground-950">编辑需求信息</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"><i className="ri-close-line" /></button>
        </div>
        <div className="space-y-3.5">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">年份</label>
            <select value={form.year} onChange={(e) => setForm((p) => ({ ...p, year: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400 cursor-pointer">
              <option value="">请选择年份</option>
              {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}年</option>)}
            </select>
          </div>
          {chips('halfYear', '半年', HALF_YEAR_OPTIONS, false)}
          {chips('month', '月份', MONTH_OPTIONS, true)}
          {chips('projectType', '项目类型', PROJECT_TYPE_OPTIONS, false)}
          {chips('channel', '通道与否', CHANNEL_OPTIONS, false)}
          {chips('provinceRequirement', '省份要求', PROVINCE_OPTIONS, true)}
          {chips('subsidy', '补贴与否', SUBSIDY_OPTIONS, false)}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">采购数量 (个)</label>
            <input type="number" min="1" value={form.quantity || ''} onChange={(e) => setForm((p) => ({ ...p, quantity: Number(e.target.value) }))} onWheel={(e) => e.currentTarget.blur()} className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">预算价格 (元/个)</label>
            <input type="text" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} placeholder="选填，例如 35.00" className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400" />
          </div>
          {chips('tradeMethod', '交易方式', TRADE_METHOD_OPTIONS, true)}
          {chips('exchange', '交易所', EXCHANGE_OPTIONS, true)}
          {chips('offerBy', '出价方', OFFER_BY_OPTIONS, true)}
          {chips('tradePrinciple', '交易原则', TRADE_PRINCIPLE_OPTIONS, false)}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">有效期至</label>
            <input type="date" value={form.expiryDate} onChange={(e) => setForm((p) => ({ ...p, expiryDate: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400 cursor-pointer" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">补充说明</label>
            <textarea value={form.otherInfo} maxLength={500} onChange={(e) => setForm((p) => ({ ...p, otherInfo: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-foreground-400 h-24 resize-none" />
            <span className="text-xs text-foreground-400">{form.otherInfo.length}/500</span>
          </div>
          {error && <p className="text-accent-600 text-xs">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} className="flex-1 py-2 rounded-lg text-sm font-medium bg-foreground-950 text-white hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">{saving ? '保存中...' : '保存修改'}</button>
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">取消</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MyDemandPage() {
  const [items, setItems] = useState<PersonalDemand[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<PersonalDemand | null>(null);
  const [confirmingDeleteIds, setConfirmingDeleteIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    profileApi.myDemands().then((data) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toggleStatus = useCallback((id: number, current: PersonalDemand['status']) => {
    const next = current === 'active' ? 'offline' : 'active';
    profileApi.setDemandStatus(id, next)
      .then(() => setItems((prev) => prev.map((d) => (d.id === id ? { ...d, status: next } : d))))
      .catch(() => {});
  }, []);

  const deleteItem = useCallback((id: number) => {
    profileApi.deleteDemand(id)
      .then(() => {
        setItems((prev) => prev.filter((d) => d.id !== id));
        setConfirmingDeleteIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      })
      .catch(() => {});
  }, []);

  const requestConfirmDelete = useCallback((id: number) => {
    setConfirmingDeleteIds((prev) => new Set(prev).add(id));
  }, []);

  const cancelConfirmDelete = useCallback((id: number) => {
    setConfirmingDeleteIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  }, []);

  const activeList = items.filter((d) => d.status === 'active');
  const offlineList = items.filter((d) => d.status === 'offline');

  return (
    <>
      <Navbar />
      <main className="w-full bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-heading text-2xl font-bold text-foreground-900">我的需求</h1>
            <Link to="/demand-publish" className="px-4 py-2 bg-secondary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-line mr-1" /> 发布需求
            </Link>
          </div>

          {editingItem && (
            <EditModal
              item={editingItem}
              onSaved={(updated) => { setItems((prev) => prev.map((d) => (d.id === updated.id ? updated : d))); setEditingItem(null); }}
              onClose={() => setEditingItem(null)}
            />
          )}

          {loading ? (
            <div className="text-center py-12"><div className="w-8 h-8 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div></div>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center"><i className="ri-shopping-cart-2-line text-2xl text-foreground-400"></i></div>
              <p className="text-foreground-500 mt-4 text-sm">暂无需求信息</p>
              <Link to="/demand-publish" className="inline-block mt-3 text-primary-600 text-sm hover:underline cursor-pointer">立即发布第一条需求</Link>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Active */}
              {activeList.length > 0 && (
                <div>
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-3">征集中 ({activeList.length})</h2>
                  <div className="space-y-3">
                    {activeList.map((item) => {
                      const projectLabel = formatFieldValue('projectType', item.projectType);
                      const channelTag = tagLabel(item.channel);
                      const tradeTag = tagLabel(item.tradeMethod);
                      const principleTag = tagLabel(item.tradePrinciple);
                      const confirmDelete = confirmingDeleteIds.has(item.id);
                      return (
                        <div key={item.id} className="bg-background-50 rounded-xl border border-background-200 p-5 hover:border-secondary-200 transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded">{projectLabel}</span>
                                <h3 className="font-heading text-base font-semibold text-foreground-900">{item.year}年度</h3>
                                {channelTag && <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-md">{channelTag}</span>}
                                {tradeTag && <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-md">{tradeTag}</span>}
                                {principleTag && <span className="px-2 py-0.5 bg-background-200 text-foreground-600 text-xs rounded-md">{principleTag}</span>}
                              </div>
                              <p className="text-foreground-500 text-xs mt-1 line-clamp-1">{item.otherInfo || '暂无补充说明'}</p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-foreground-500">
                                <span>预算 &yen;{item.price}/个</span>
                                <span>{item.quantity.toLocaleString()} 个</span>
                                <span>有效期至 {item.expiryDate}</span>
                                <span>{item.inquiries} 次响应</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button onClick={() => setEditingItem(item)} className="px-3 py-1.5 border border-background-300 text-foreground-600 text-xs rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-edit-line mr-1" />编辑</button>
                              <button onClick={() => toggleStatus(item.id, item.status)} className="px-3 py-1.5 border border-accent-300 text-accent-600 text-xs rounded-md hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-arrow-down-circle-line mr-1" />结束征集</button>
                              {confirmDelete ? (
                                <div className="flex items-center gap-1">
                                  <button onClick={() => deleteItem(item.id)} className="px-2.5 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 cursor-pointer whitespace-nowrap">确认</button>
                                  <button onClick={() => cancelConfirmDelete(item.id)} className="px-2 py-1.5 text-foreground-400 text-xs rounded-md hover:text-foreground-600 cursor-pointer">取消</button>
                                </div>
                              ) : (
                                <button onClick={() => requestConfirmDelete(item.id)} className="px-3 py-1.5 border border-background-300 text-foreground-400 text-xs rounded-md hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-delete-bin-line mr-1" />删除</button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Offline */}
              {offlineList.length > 0 && (
                <div>
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-3">已结束 ({offlineList.length})</h2>
                  <div className="space-y-3">
                    {offlineList.map((item) => {
                      const projectLabel = formatFieldValue('projectType', item.projectType);
                      const channelTag = tagLabel(item.channel);
                      const tradeTag = tagLabel(item.tradeMethod);
                      const confirmDelete = confirmingDeleteIds.has(item.id);
                      return (
                        <div key={item.id} className="bg-background-100 rounded-xl border border-background-200 p-5 opacity-75">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <span className="px-2 py-0.5 bg-foreground-200 text-foreground-600 text-xs font-medium rounded">{projectLabel}</span>
                                <h3 className="font-heading text-base font-semibold text-foreground-700">{item.year}年度</h3>
                                {channelTag && <span className="px-2 py-0.5 bg-foreground-200 text-foreground-500 text-xs rounded-md">{channelTag}</span>}
                                {tradeTag && <span className="px-2 py-0.5 bg-foreground-200 text-foreground-500 text-xs rounded-md">{tradeTag}</span>}
                              </div>
                              <p className="text-foreground-400 text-xs mt-1 line-clamp-1">{item.otherInfo || '暂无补充说明'}</p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-foreground-400">
                                <span>预算 &yen;{item.price}/个</span>
                                <span>{item.quantity.toLocaleString()} 个</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button onClick={() => setEditingItem(item)} className="px-3 py-1.5 border border-background-300 text-foreground-600 text-xs rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-edit-line mr-1" />编辑</button>
                              <button onClick={() => toggleStatus(item.id, item.status)} className="px-3 py-1.5 border border-secondary-300 text-secondary-600 text-xs rounded-md hover:bg-secondary-50 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-arrow-up-circle-line mr-1" />重新征集</button>
                              {confirmDelete ? (
                                <div className="flex items-center gap-1">
                                  <button onClick={() => deleteItem(item.id)} className="px-2.5 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 cursor-pointer whitespace-nowrap">确认</button>
                                  <button onClick={() => cancelConfirmDelete(item.id)} className="px-2 py-1.5 text-foreground-400 text-xs rounded-md hover:text-foreground-600 cursor-pointer">取消</button>
                                </div>
                              ) : (
                                <button onClick={() => requestConfirmDelete(item.id)} className="px-3 py-1.5 border border-background-300 text-foreground-400 text-xs rounded-md hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-delete-bin-line mr-1" />删除</button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
